// packages/shared-components/src/components/Tabs/Tabs.tsx
import { Children, ReactElement, type CSSProperties } from "react";
import { TabsProps, TabItemProps } from "./types";
import styles from "./Tabs.module.css";

const formatCssSize = (value: number | string) =>
  typeof value === "number" ? `${value}px` : value;

export function Tabs({
  activeKey,
  onChange,
  children,
  placement = "top",
  className = "",
  height = 36,
  outerBorderRadius,
  innerBorderRadius,
  borderRadius,
  sliderClassName = "shared-active-glow",
  disabled,
  onDisabledTabClick,
}: TabsProps) {
  const tabs = Children.toArray(children) as ReactElement<TabItemProps>[];
  const resolvedOuterBorderRadius = outerBorderRadius ?? borderRadius ?? 8;
  const resolvedInnerBorderRadius = innerBorderRadius ?? borderRadius ?? 6;
  const activeTab = tabs.find((tab) => tab.props.tabKey === activeKey);
  const activeIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.props.tabKey === activeKey)
  );
  const count = tabs.length;
  const pillVars =
    count > 0
      ? ({
          "--tab-count": count,
          "--tab-active-index": activeIndex,
          "--tab-height": `${height}px`,
          "--tab-outer-border-radius": formatCssSize(resolvedOuterBorderRadius),
          "--tab-inner-border-radius": formatCssSize(resolvedInnerBorderRadius),
        } as CSSProperties)
      : undefined;

  const tabList = (
    <div className={`${styles.tabList} ${styles.pill}`} style={pillVars}>
      {count > 0 && (
        <span
          className={`${styles.pillSlider} ${sliderClassName}`}
          aria-hidden
        />
      )}
      {tabs.map((tab) => {
        const tabDisabled = !!(disabled || tab.props.disabled);
        const allowDisabledClick =
          tabDisabled && typeof onDisabledTabClick === "function";
        return (
          <button
            key={tab.props.tabKey}
            type="button"
            disabled={tabDisabled && !allowDisabledClick}
            aria-disabled={tabDisabled}
            className={`${styles.tab} ${
              activeKey === tab.props.tabKey ? styles.active : ""
            } ${tabDisabled ? styles.disabled : ""}
          ${count === 1 ? styles.oneTab : ""}`}
            onClick={() => {
              if (!tabDisabled) {
                onChange(tab.props.tabKey);
                return;
              }
              if (allowDisabledClick) {
                onDisabledTabClick(tab.props.tabKey);
              }
            }}
          >
            {tab.props.label}
          </button>
        );
      })}
    </div>
  );

  const tabContent = <div className={styles.tabContent}>{activeTab}</div>;

  return (
    <div className={`${styles.tabs} ${className}`}>
      {placement === "top" ? (
        <>
          {tabList}
          {tabContent}
        </>
      ) : (
        <>
          {tabContent}
          {tabList}
        </>
      )}
    </div>
  );
}
